<template>
  <v-container fluid>
    <v-card>
      <v-card-title class="text-h4 primary white--text">
        <v-icon left color="white" large>mdi-clock-alert-outline</v-icon>
        Pending Njangi Groups for Validation
      </v-card-title>

      <!-- Filters and Statistics -->
      <v-card-text>
        <v-row>
          <v-col cols="12" md="8">
            <v-chip-group v-model="selectedCycle" column active-class="primary--text">
              <v-chip filter outlined @click="filterByCycle(null)" :value="null">
                <v-icon left>mdi-filter-off</v-icon>
                All ({{ statistics.Total }})
              </v-chip>
              <v-chip filter outlined color="blue" @click="filterByCycle('Daily')" :value="'Daily'">
                <v-icon left>mdi-calendar-today</v-icon>
                Daily ({{ statistics.Daily }})
              </v-chip>
              <v-chip filter outlined color="orange" @click="filterByCycle('Weekly')" :value="'Weekly'">
                <v-icon left>mdi-calendar-week</v-icon>
                Weekly ({{ statistics.Weekly }})
              </v-chip>
              <v-chip filter outlined color="purple" @click="filterByCycle('Monthly')" :value="'Monthly'">
                <v-icon left>mdi-calendar-month</v-icon>
                Monthly ({{ statistics.Monthly }})
              </v-chip>
            </v-chip-group>
          </v-col>
          <v-col cols="12" md="4" class="text-right">
            <v-btn color="primary" @click="loadPendingGroups" :loading="loading">
              <v-icon left>mdi-refresh</v-icon>
              Refresh
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Loading -->
      <v-card-text v-if="loading" class="text-center py-12">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        <p class="mt-4 text-h6">Loading groups...</p>
      </v-card-text>

      <!-- No Groups -->
      <v-card-text v-else-if="!loading && groups.length === 0" class="text-center py-12">
        <v-icon color="success" size="100">mdi-check-circle-outline</v-icon>
        <p class="text-h5 mt-4">No pending groups</p>
        <p class="text-subtitle-1 grey--text">All groups have been validated!</p>
      </v-card-text>

      <!-- Groups List -->
      <v-card-text v-else>
        <v-alert type="info" dense outlined class="mb-4">
          <strong>{{ groups.length }}</strong> group(s) pending validation
        </v-alert>

        <v-expansion-panels v-model="expandedPanels" multiple>
          <v-expansion-panel v-for="group in groups" :key="group.id" class="mb-3">
            <v-expansion-panel-header>
              <v-row align="center" no-gutters>
                <v-col cols="auto" class="mr-3">
                  <v-avatar size="60">
                    <v-img v-if="group.thumbnail" :src="group.thumbnail"></v-img>
                    <v-icon v-else size="40">mdi-account-group</v-icon>
                  </v-avatar>
                </v-col>
                <v-col>
                  <div class="text-h6">{{ group.name }}</div>
                  <div class="text-caption grey--text">
                    <v-chip x-small :color="getCycleColor(group.settings.collecting_cycle)" dark class="mr-2">
                      {{ group.settings.collecting_cycle }}
                    </v-chip>
                    {{ formatAmount(group.settings.contribution_amount) }} FCFA
                    • {{ group.member_count }} member(s)
                  </div>
                </v-col>
                <v-col cols="auto">
                  <v-btn color="success" small @click.stop="approveGroup(group.id)"
                         :loading="approvingGroup === group.id" class="mr-2">
                    <v-icon left small>mdi-check</v-icon>
                    Approve
                  </v-btn>
                  <v-btn color="error" small @click.stop="openRejectDialog(group)"
                         :loading="rejectingGroup === group.id">
                    <v-icon left small>mdi-close</v-icon>
                    Reject
                  </v-btn>
                </v-col>
              </v-row>
            </v-expansion-panel-header>

            <v-expansion-panel-content>
              <v-divider class="mb-4"></v-divider>

              <!-- Group Details -->
              <v-row>
                <v-col cols="12" md="6">
                  <v-card outlined>
                    <v-card-subtitle class="font-weight-bold">
                      <v-icon left>mdi-information</v-icon>
                      General Information
                    </v-card-subtitle>
                    <v-card-text>
                      <v-simple-table dense>
                        <tbody>
                          <tr>
                            <td class="font-weight-bold">Administrator:</td>
                            <td>{{ group.admin.username }} ({{ group.admin.email }})</td>
                          </tr>
                          <tr>
                            <td class="font-weight-bold">Contact:</td>
                            <td>{{ group.njangi_contact }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-bold">Creation Date:</td>
                            <td>{{ group.time_created }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-bold">Njangi Account:</td>
                            <td>{{ formatAmount(group.njangi_account) }} FCFA</td>
                          </tr>
                        </tbody>
                      </v-simple-table>
                    </v-card-text>
                  </v-card>
                </v-col>

                <v-col cols="12" md="6">
                  <v-card outlined>
                    <v-card-subtitle class="font-weight-bold d-flex justify-space-between align-center">
                      <div>
                        <v-icon left>mdi-cog</v-icon>
                        Settings
                      </div>
                      <v-btn x-small color="warning" @click="openDateAdjustDialog(group)">
                        <v-icon left x-small>mdi-calendar-edit</v-icon>
                        Adjust Dates
                      </v-btn>
                    </v-card-subtitle>
                    <v-card-text>
                      <v-simple-table dense>
                        <tbody>
                          <tr>
                            <td class="font-weight-bold">Cycle:</td>
                            <td>
                              <v-chip small :color="getCycleColor(group.settings.collecting_cycle)" dark>
                                {{ group.settings.collecting_cycle }}
                              </v-chip>
                            </td>
                          </tr>
                          <tr>
                            <td class="font-weight-bold">Contribution:</td>
                            <td>{{ formatAmount(group.settings.contribution_amount) }} FCFA</td>
                          </tr>
                          <tr>
                            <td class="font-weight-bold">Start Date:</td>
                            <td>
                              <span :class="getDateValidationClass(group.settings.start_date, group.settings.collecting_cycle)">
                                {{ group.settings.start_date }}
                              </span>
                              <v-icon v-if="!isValidStartDate(group.settings.start_date, group.settings.collecting_cycle)"
                                      color="warning" small class="ml-1">
                                mdi-alert-circle
                              </v-icon>
                            </td>
                          </tr>
                          <tr>
                            <td class="font-weight-bold">Collection Date:</td>
                            <td>{{ group.settings.collection_date || 'N/A' }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-bold">Group Code:</td>
                            <td><code>{{ group.settings.group_code }}</code></td>
                          </tr>
                          <tr>
                            <td class="font-weight-bold">Current Position:</td>
                            <td>{{ group.settings.current_collect_position }}</td>
                          </tr>
                        </tbody>
                      </v-simple-table>
                    </v-card-text>
                  </v-card>
                </v-col>

                <!-- Information & Rules -->
                <v-col cols="12" v-if="group.information || group.rules">
                  <v-card outlined>
                    <v-tabs v-model="activeTab[group.id]" background-color="primary" dark>
                      <v-tab v-if="group.information">
                        <v-icon left>mdi-text</v-icon>
                        Information
                      </v-tab>
                      <v-tab v-if="group.rules">
                        <v-icon left>mdi-gavel</v-icon>
                        Rules
                      </v-tab>
                      <v-tab v-if="group.history">
                        <v-icon left>mdi-history</v-icon>
                        History
                      </v-tab>
                    </v-tabs>
                    <v-tabs-items v-model="activeTab[group.id]">
                      <v-tab-item v-if="group.information">
                        <v-card-text>
                          <div v-html="group.information"></div>
                        </v-card-text>
                      </v-tab-item>
                      <v-tab-item v-if="group.rules">
                        <v-card-text>
                          <div v-html="group.rules"></div>
                        </v-card-text>
                      </v-tab-item>
                      <v-tab-item v-if="group.history">
                        <v-card-text>
                          <div v-html="group.history"></div>
                        </v-card-text>
                      </v-tab-item>
                    </v-tabs-items>
                  </v-card>
                </v-col>

                <!-- Members List -->
                <v-col cols="12">
                  <v-card outlined>
                    <v-card-subtitle class="font-weight-bold d-flex align-center justify-space-between">
                      <div>
                        <v-icon left>mdi-account-multiple</v-icon>
                        Members ({{ group.members.length }})
                      </div>
                      <v-btn small color="primary" @click="openAddMemberDialog(group)">
                        <v-icon left small>mdi-account-plus</v-icon>
                        Add Member
                      </v-btn>
                    </v-card-subtitle>
                    <v-card-text>
                      <v-data-table :headers="memberHeaders" :items="group.members"
                                    dense :items-per-page="10">
                        <template v-slot:item.username="{ item }">
                          <div class="d-flex align-center">
                            <v-avatar size="32" class="mr-2">
                              <v-icon>mdi-account-circle</v-icon>
                            </v-avatar>
                            <div>
                              <div class="font-weight-medium">{{ item.username }}</div>
                              <div class="text-caption grey--text">{{ item.email }}</div>
                            </div>
                          </div>
                        </template>

                        <template v-slot:item.position_number="{ item }">
                          <v-chip small outlined>
                            Position {{ item.position_number || 'N/A' }}
                          </v-chip>
                        </template>

                        <template v-slot:item.position_name="{ item }">
                          <v-chip small :color="item.position_name === 'Admin' ? 'error' : 'primary'" dark>
                            {{ item.position_name || 'Member' }}
                          </v-chip>
                        </template>

                        <template v-slot:item.status="{ item }">
                          <v-chip x-small :color="item.status === 'Ongoing' ? 'success' : 'warning'" dark>
                            {{ item.status }}
                          </v-chip>
                        </template>

                        <template v-slot:item.actions="{ item }">
                          <v-btn icon small color="primary" @click="openEditPositionDialog(item, group)">
                            <v-icon small>mdi-pencil</v-icon>
                          </v-btn>
                        </template>
                      </v-data-table>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>
    </v-card>

    <!-- Date Adjustment Dialog -->
    <v-dialog v-model="dateAdjustDialog" max-width="700" persistent>
      <v-card>
        <v-card-title class="headline warning white--text">
          <v-icon left color="white">mdi-calendar-edit</v-icon>
          Adjust Group Dates
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="info" outlined dense class="mb-4">
            <strong>Group:</strong> {{ adjustingGroup.name }}<br>
            <strong>Cycle:</strong>
            <v-chip x-small :color="getCycleColor(adjustingGroup.cycle)" dark class="ml-1">
              {{ adjustingGroup.cycle }}
            </v-chip>
          </v-alert>

          <!-- Rules Display -->
          <v-alert :type="getDateValidationType(adjustingGroup.cycle)" dense class="mb-4">
            <div class="font-weight-bold mb-2">📋 Validation Rules for {{ adjustingGroup.cycle }}:</div>
            <ul class="pl-4">
              <li v-if="adjustingGroup.cycle === 'Monthly'">
                <strong>start_date</strong> must be the <strong>1st of the month</strong>
                <br>If different → adjusted to 1st of next month
              </li>
              <li v-if="adjustingGroup.cycle === 'Weekly'">
                <strong>start_date</strong> must be a <strong>Monday</strong>
                <br>If different → adjusted to Monday of current week
              </li>
              <li v-if="adjustingGroup.cycle === 'Daily'">
                <strong>start_date</strong> = provided date or today
              </li>
              <li class="mt-1">
                <strong>collection_date</strong> will be calculated automatically
              </li>
            </ul>
          </v-alert>

          <!-- Current vs New Dates -->
          <v-row>
            <v-col cols="12" md="6">
              <v-card outlined>
                <v-card-subtitle class="font-weight-bold">📅 Current Dates</v-card-subtitle>
                <v-card-text>
                  <div class="mb-2">
                    <strong>Start Date:</strong>
                    <div :class="isValidStartDate(adjustingGroup.current_start_date, adjustingGroup.cycle) ? 'success--text' : 'error--text'">
                      {{ adjustingGroup.current_start_date }}
                      <v-icon v-if="!isValidStartDate(adjustingGroup.current_start_date, adjustingGroup.cycle)"
                              color="error" small>
                        mdi-close-circle
                      </v-icon>
                      <v-icon v-else color="success" small>mdi-check-circle</v-icon>
                    </div>
                  </div>
                  <div>
                    <strong>Collection Date:</strong>
                    <div>{{ adjustingGroup.current_collection_date || 'N/A' }}</div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" md="6">
              <v-card outlined color="success lighten-5">
                <v-card-subtitle class="font-weight-bold">✨ Adjusted Dates (Preview)</v-card-subtitle>
                <v-card-text>
                  <div class="mb-2">
                    <strong>New Start Date:</strong>
                    <div class="success--text font-weight-bold">
                      {{ previewDates.start_date }}
                      <v-icon color="success" small>mdi-check-circle</v-icon>
                    </div>
                  </div>
                  <div>
                    <strong>New Collection Date:</strong>
                    <div class="success--text font-weight-bold">{{ previewDates.collection_date }}</div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Date Picker -->
          <v-row class="mt-4">
            <v-col cols="12">
              <v-menu v-model="datePickerMenu" :close-on-content-click="false"
                      transition="scale-transition" offset-y min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field v-model="newStartDate" label="New Start Date"
                                prepend-icon="mdi-calendar" readonly v-bind="attrs" v-on="on"
                                outlined dense hint="Select a date, it will be adjusted according to the rules"
                                persistent-hint></v-text-field>
                </template>
                <v-date-picker v-model="newStartDate" @input="datePickerMenu = false; calculatePreviewDates()"
                               :min="getTodayDate()"></v-date-picker>
              </v-menu>
            </v-col>
          </v-row>

          <!-- Warning if dates will be adjusted -->
          <v-alert v-if="willBeAdjusted" type="warning" dense class="mt-4">
            <v-icon left>mdi-information</v-icon>
            The date will be automatically adjusted to respect the <strong>{{ adjustingGroup.cycle }}</strong> cycle rules
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDateAdjustDialog">Cancel</v-btn>
          <v-btn color="warning" @click="confirmDateAdjustment" :loading="adjustingDates">
            <v-icon left>mdi-content-save</v-icon>
            Apply Changes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Reject Group Dialog -->
    <v-dialog v-model="rejectDialog" max-width="600">
      <v-card>
        <v-card-title class="headline error white--text">
          <v-icon left color="white">mdi-alert-circle</v-icon>
          Reject Group
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="warning" outlined dense class="mb-4">
            <strong>Warning!</strong> This action is irreversible. The group will be permanently deleted.
          </v-alert>

          <p class="mb-3">
            <strong>Group:</strong> {{ rejectingGroupData.name }}<br>
            <strong>Admin:</strong> {{ rejectingGroupData.admin_username }}
          </p>

          <v-textarea v-model="rejectionReason" label="Rejection Reason *" outlined rows="4" counter="500"
                      :rules="[v => !!v || 'Reason is required', v => (v && v.length >= 10) || 'Minimum 10 characters']"
                      hint="Clearly explain why this group is being rejected" persistent-hint></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeRejectDialog">Cancel</v-btn>
          <v-btn color="error" @click="confirmReject" :loading="rejectingGroup"
                 :disabled="!rejectionReason || rejectionReason.length < 10">
            <v-icon left>mdi-delete</v-icon>
            Confirm Rejection
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Position Dialog -->
    <v-dialog v-model="editPositionDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">Edit Position</v-card-title>
        <v-card-text>
          <v-text-field v-model="editingPosition.new_position" type="number" label="New Position"
                        outlined dense :rules="[v => !!v || 'Position required', v => v > 0 || 'Position must be > 0']"></v-text-field>
          <p class="text-caption grey--text">
            Member: <strong>{{ editingPosition.username }}</strong>
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="editPositionDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="updatePosition" :loading="updatingPosition">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add Member Dialog -->
    <v-dialog v-model="addMemberDialog" max-width="600">
      <v-card>
        <v-card-title class="headline">Add Member</v-card-title>
        <v-card-text>
          <v-text-field v-model="userSearchQuery" label="Search User" outlined dense
                        prepend-inner-icon="mdi-magnify" @input="searchUsers" :loading="searchingUsers"></v-text-field>

          <v-list v-if="searchResults.length > 0" dense>
            <v-list-item v-for="user in searchResults" :key="user.id" @click="selectUser(user)"
                         :class="{ 'primary lighten-4': selectedUser && selectedUser.id === user.id }">
              <v-list-item-avatar>
                <v-icon>mdi-account-circle</v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>{{ user.username }}</v-list-item-title>
                <v-list-item-subtitle>{{ user.email }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>

          <v-text-field v-if="selectedUser" v-model="newMemberPosition" type="number"
                        label="Position (optional)" outlined dense
                        hint="Leave empty for automatic assignment" persistent-hint class="mt-4"></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeAddMemberDialog">Cancel</v-btn>
          <v-btn color="primary" @click="addMember" :disabled="!selectedUser" :loading="addingMember">Add</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>
<script>
import axios from 'axios'

export default {
  name: 'PendingGroupsManagement',

  data() {
    return {
      loading: false,
      groups: [],
      expandedPanels: [],
      activeTab: {},
      approvingGroup: null,

      selectedCycle: null,
      currentFilter: null,
      statistics: {
        Total: 0,
        Daily: 0,
        Weekly: 0,
        Monthly: 0
      },

      // Date adjustment
      dateAdjustDialog: false,
      datePickerMenu: false,
      adjustingDates: false,
      adjustingGroup: {
        id: null,
        name: '',
        cycle: '',
        current_start_date: '',
        current_collection_date: ''
      },
      newStartDate: '',
      previewDates: {
        start_date: '',
        collection_date: ''
      },

      rejectDialog: false,
      rejectingGroup: null,
      rejectingGroupData: {
        id: null,
        name: '',
        admin_username: ''
      },
      rejectionReason: '',

      memberHeaders: [
        { text: 'Utilisateur', value: 'username', sortable: true },
        { text: 'Position', value: 'position_number', align: 'center' },
        { text: 'Rôle', value: 'position_name', align: 'center' },
        { text: 'Statut', value: 'status', align: 'center' },
        { text: 'Date', value: 'time_created' },
        { text: 'Actions', value: 'actions', sortable: false, align: 'center' },
      ],

      editPositionDialog: false,
      editingPosition: {
        membership_id: null,
        username: '',
        current_position: null,
        new_position: null,
      },
      updatingPosition: false,

      addMemberDialog: false,
      currentGroup: null,
      userSearchQuery: '',
      searchResults: [],
      searchingUsers: false,
      selectedUser: null,
      newMemberPosition: '',
      addingMember: false,
      searchTimeout: null,
    }
  },

  computed: {
    willBeAdjusted() {
      if (!this.newStartDate || !this.adjustingGroup.cycle) return false
      return !this.isValidStartDate(this.newStartDate, this.adjustingGroup.cycle)
    }
  },

  mounted() {
    this.loadStatistics()
    this.loadPendingGroups()
  },

  methods: {
    // ============ DATE VALIDATION METHODS ============
    isValidStartDate(dateStr, cycle) {
      if (!dateStr) return false
      const date = new Date(dateStr)

      if (cycle === 'Monthly') {
        return date.getDate() === 1
      } else if (cycle === 'Weekly') {
        return date.getDay() === 1 // Lundi
      } else if (cycle === 'Daily') {
        return true
      }
      return false
    },

    getDateValidationClass(dateStr, cycle, type) {
      if (!dateStr) return ''
      if (type === 'start') {
        return this.isValidStartDate(dateStr, cycle) ? 'success--text' : 'error--text'
      }
      return ''
    },

    getDateValidationType(cycle) {
      const types = {
        'Monthly': 'info',
        'Weekly': 'warning',
        'Daily': 'success'
      }
      return types[cycle] || 'info'
    },

    getTodayDate() {
      return new Date().toISOString().split('T')[0]
    },

    adjustDateByCycle(dateStr, cycle) {
      const date = new Date(dateStr)
      let adjustedDate = new Date(date)
      let collectionDate = new Date(date)

      if (cycle === 'Monthly') {
        // Start date doit être le 1er du mois
        if (date.getDate() !== 1) {
          // Ajuster au 1er du mois suivant
          adjustedDate = new Date(date.getFullYear(), date.getMonth() + 1, 1)
        } else {
          adjustedDate = new Date(date.getFullYear(), date.getMonth(), 1)
        }
        // Collection date = 2 du mois suivant
        collectionDate = new Date(adjustedDate.getFullYear(), adjustedDate.getMonth() + 1, 2, 12, 0, 0)
      }
      else if (cycle === 'Weekly') {
        // Start date doit être un lundi
        const dayOfWeek = date.getDay()
        const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
        adjustedDate = new Date(date)
        adjustedDate.setDate(date.getDate() + daysToMonday)
        adjustedDate.setHours(0, 0, 0, 0)

        // Collection date = lundi suivant
        collectionDate = new Date(adjustedDate)
        collectionDate.setDate(adjustedDate.getDate() + 7)
        collectionDate.setHours(12, 0, 0, 0)
      }
      else if (cycle === 'Daily') {
        adjustedDate = new Date(date)
        adjustedDate.setHours(0, 0, 0, 0)

        collectionDate = new Date(adjustedDate)
        collectionDate.setDate(adjustedDate.getDate() + 1)
        collectionDate.setHours(12, 0, 0, 0)
      }

      return {
        start_date: adjustedDate.toISOString().split('T')[0],
        collection_date: collectionDate.toISOString().replace('T', ' ').split('.')[0]
      }
    },

    calculatePreviewDates() {
      if (!this.newStartDate || !this.adjustingGroup.cycle) return

      const adjusted = this.adjustDateByCycle(this.newStartDate, this.adjustingGroup.cycle)
      this.previewDates = adjusted
    },

    // ============ DIALOG METHODS ============
    openDateAdjustDialog(group) {
      this.adjustingGroup = {
        id: group.id,
        name: group.name,
        cycle: group.settings.collecting_cycle,
        current_start_date: group.settings.start_date,
        current_collection_date: group.settings.collection_date
      }
      this.newStartDate = group.settings.start_date ? group.settings.start_date.split(' ')[0] : this.getTodayDate()
      this.calculatePreviewDates()
      this.dateAdjustDialog = true
    },

    closeDateAdjustDialog() {
      this.dateAdjustDialog = false
      this.adjustingGroup = { id: null, name: '', cycle: '', current_start_date: '', current_collection_date: '' }
      this.newStartDate = ''
      this.previewDates = { start_date: '', collection_date: '' }
    },

    async confirmDateAdjustment() {
      if (!this.newStartDate) {
        this.$store.commit('setSnackBarMessage', 'Veuillez sélectionner une date')
        this.$store.commit('setSnackBarColor', 'warning')
        this.$store.commit('activateSnackBar', true)
        return
      }

      this.adjustingDates = true
      try {
        const response = await axios.post(
          `/api/v1/manager/njangi/adjust-dates/${this.adjustingGroup.id}/`,
          { start_date: this.newStartDate }
        )

        this.$store.commit('setSnackBarMessage', response.data.message || 'Dates ajustées avec succès')
        this.$store.commit('setSnackBarColor', 'success')
        this.$store.commit('activateSnackBar', true)

        this.closeDateAdjustDialog()
        await this.loadPendingGroups()
      } catch (error) {
        console.error('Error adjusting dates:', error)
        this.$store.commit('setSnackBarMessage', error.response?.data?.error || 'Erreur lors de l\'ajustement')
        this.$store.commit('setSnackBarColor', 'red darken-4')
        this.$store.commit('activateSnackBar', true)
      } finally {
        this.adjustingDates = false
      }
    },

    // ============ LOAD DATA METHODS ============
    async loadStatistics() {
      try {
        const response = await axios.get('/api/v1/manager/njangi/cycle-statistics/')
        this.statistics = response.data.statistics
      } catch (error) {
        console.error('Error loading statistics:', error)
      }
    },

    async loadPendingGroups() {
      this.loading = true
      try {
        let url = '/api/v1/manager/njangi/pending-groups/'
        if (this.currentFilter) {
          url += `?cycle=${this.currentFilter}`
        }

        const response = await axios.get(url)
        this.groups = response.data.groups
        console.log(`✅ Loaded ${this.groups.length} pending groups`)
      } catch (error) {
        console.error('Error loading groups:', error)
        this.$store.commit('setSnackBarMessage', 'Erreur lors du chargement des groupes')
        this.$store.commit('setSnackBarColor', 'red darken-4')
        this.$store.commit('activateSnackBar', true)
      } finally {
        this.loading = false
      }
    },

    filterByCycle(cycle) {
      this.currentFilter = cycle
      this.selectedCycle = cycle
      this.loadPendingGroups()
    },

    async approveGroup(groupId) {
      if (!confirm('Êtes-vous sûr de vouloir approuver ce groupe ?')) {
        return
      }

      this.approvingGroup = groupId
      try {
        const response = await axios.post(`/api/v1/manager/njangi/approve-group/${groupId}/`)

        this.$store.commit('setSnackBarMessage', response.data.message)
        this.$store.commit('setSnackBarColor', 'success')
        this.$store.commit('activateSnackBar', true)

        await this.loadStatistics()
        await this.loadPendingGroups()
      } catch (error) {
        console.error('Error approving group:', error)
        this.$store.commit('setSnackBarMessage', error.response?.data?.error || 'Erreur lors de l\'approbation')
        this.$store.commit('setSnackBarColor', 'red darken-4')
        this.$store.commit('activateSnackBar', true)
      } finally {
        this.approvingGroup = null
      }
    },

    openRejectDialog(group) {
      this.rejectingGroupData = {
        id: group.id,
        name: group.name,
        admin_username: group.admin ? group.admin.username : 'N/A'
      }
      this.rejectionReason = ''
      this.rejectDialog = true
    },

    closeRejectDialog() {
      this.rejectDialog = false
      this.rejectingGroupData = { id: null, name: '', admin_username: '' }
      this.rejectionReason = ''
    },

    async confirmReject() {
      if (!this.rejectionReason || this.rejectionReason.length < 10) {
        this.$store.commit('setSnackBarMessage', 'Veuillez fournir une raison détaillée (min 10 caractères)')
        this.$store.commit('setSnackBarColor', 'warning')
        this.$store.commit('activateSnackBar', true)
        return
      }

      this.rejectingGroup = this.rejectingGroupData.id
      try {
        const response = await axios.post(
          `/api/v1/manager/njangi/reject-group/${this.rejectingGroupData.id}/`,
          { reason: this.rejectionReason }
        )

        this.$store.commit('setSnackBarMessage', response.data.message)
        this.$store.commit('setSnackBarColor', 'success')
        this.$store.commit('activateSnackBar', true)

        this.closeRejectDialog()
        await this.loadStatistics()
        await this.loadPendingGroups()
      } catch (error) {
        console.error('Error rejecting group:', error)
        this.$store.commit('setSnackBarMessage', error.response?.data?.error || 'Erreur lors du rejet')
        this.$store.commit('setSnackBarColor', 'red darken-4')
        this.$store.commit('activateSnackBar', true)
      } finally {
        this.rejectingGroup = null
      }
    },

    openEditPositionDialog(member, group) {
      this.editingPosition = {
        membership_id: member.id,
        username: member.username,
        current_position: member.position_number,
        new_position: member.position_number,
        group_id: group.id,
      }
      this.editPositionDialog = true
    },

    async updatePosition() {
      this.updatingPosition = true
      try {
        const response = await axios.post(
          `/api/v1/manager/njangi/update-position/${this.editingPosition.membership_id}/`,
          { position_number: this.editingPosition.new_position }
        )

        this.$store.commit('setSnackBarMessage', response.data.message)
        this.$store.commit('setSnackBarColor', 'success')
        this.$store.commit('activateSnackBar', true)

        this.editPositionDialog = false
        await this.loadPendingGroups()
      } catch (error) {
        console.error('Error updating position:', error)
        this.$store.commit('setSnackBarMessage', error.response?.data?.error || 'Erreur lors de la mise à jour')
        this.$store.commit('setSnackBarColor', 'red darken-4')
        this.$store.commit('activateSnackBar', true)
      } finally {
        this.updatingPosition = false
      }
    },

    openAddMemberDialog(group) {
      this.currentGroup = group
      this.addMemberDialog = true
    },

    closeAddMemberDialog() {
      this.addMemberDialog = false
      this.userSearchQuery = ''
      this.searchResults = []
      this.selectedUser = null
      this.newMemberPosition = ''
      this.currentGroup = null
    },

    searchUsers() {
      clearTimeout(this.searchTimeout)

      if (this.userSearchQuery.length < 2) {
        this.searchResults = []
        return
      }

      this.searchTimeout = setTimeout(async () => {
        this.searchingUsers = true
        try {
          const response = await axios.get('/api/v1/manager/njangi/search-users/', {
            params: { q: this.userSearchQuery }
          })
          this.searchResults = response.data.users
        } catch (error) {
          console.error('Error searching users:', error)
        } finally {
          this.searchingUsers = false
        }
      }, 300)
    },

    selectUser(user) {
      this.selectedUser = user
    },

    async addMember() {
      if (!this.selectedUser || !this.currentGroup) return

      this.addingMember = true
      try {
        const payload = { user_id: this.selectedUser.id }
        if (this.newMemberPosition) {
          payload.position_number = parseInt(this.newMemberPosition)
        }

        const response = await axios.post(
          `/api/v1/manager/njangi/add-member/${this.currentGroup.id}/`,
          payload
        )

        this.$store.commit('setSnackBarMessage', response.data.message)
        this.$store.commit('setSnackBarColor', 'success')
        this.$store.commit('activateSnackBar', true)

        this.closeAddMemberDialog()
        await this.loadPendingGroups()
      } catch (error) {
        console.error('Error adding member:', error)
        this.$store.commit('setSnackBarMessage', error.response?.data?.error || 'Erreur lors de l\'ajout')
        this.$store.commit('setSnackBarColor', 'red darken-4')
        this.$store.commit('activateSnackBar', true)
      } finally {
        this.addingMember = false
      }
    },

    formatAmount(amount) {
      return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
    },

    getCycleColor(cycle) {
      const colors = { 'Daily': 'blue', 'Weekly': 'orange', 'Monthly': 'purple' }
      return colors[cycle] || 'grey'
    },
  }
}
</script>

<style scoped>
code {
  background-color: #f5f5f5;
  padding: 2px 6px;
  border-radius: 3px;
  font-family: monospace;
}
</style>
